import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Suspense } from 'react';

import * as api from '@gitbook/api-client';
import { isLegacySiteType } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomLink,
    Card,
    DescriptionDetail,
    DescriptionList,
    DescriptionListIcon,
    DescriptionTerm,
    Layout,
    Link,
    Page,
    PageGradientWallpaper,
    PrettyURL,
} from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import {
    CopyableURL,
    getSiteVisibilityIcon,
    getSiteVisibilityLabel,
    SiteInsightsOverview,
    SiteOverviewSectionLink,
    SitePreviewIframe,
    SiteTypeBadge,
    SiteTypeHoverCard,
} from '../../../Components';
import { apiQuery, type SiteCapability } from '../../../state';
import { AdsReviewStatusAlert } from './Ads';
import { BasicSiteUsingPaidFeaturesAlert } from './BasicSiteUsingPaidFeaturesAlert';
import { SiteGetStarted, useIsSiteGetStartedDismissed } from './SiteGetStarted';
import { SiteImportBanner } from './SiteImportBanner';
import { SiteRandomFeatureCard } from './SiteRandomFeatureCard';
import { SiteStructureOverview } from './SiteStructureOverview';
import { SiteTrialBanner } from './SiteTrialBanner';

export function SiteOverview(props: {
    site: api.Site;
    siteStructure: api.SiteStructure;
    sitePermissions: SiteCapability;
    siteCustomization: api.SiteCustomizationSettings;
    organization: api.Organization;
    orgUsageMembers: api.OrganizationUsageMembers | null;
}) {
    const {
        site,
        siteStructure,
        sitePermissions,
        siteCustomization,
        organization,
        orgUsageMembers,
    } = props;
    const VisibilityIcon = getSiteVisibilityIcon(site.visibility);

    return (
        <Page className="flex flex-1 flex-col">
            <PageGradientWallpaper
                gradientColor={siteCustomization.styling.primaryColor}
                className="flex"
            >
                <Layout wide>
                    <main className="grid grid-cols-6 gap-8 py-[--page-py] [--page-py:theme(spacing.6)] lg:[--page-py:theme(spacing.10)]">
                        <div className="col-span-6 flex flex-col gap-4 lg:col-span-3 xl:col-span-2">
                            <Card className="flex flex-col gap-4 py-4">
                                <div className="flex flex-col gap-4 px-4">
                                    <AtomLink
                                        href={{
                                            screen: {
                                                type: 'site',
                                                organization: organization.id,
                                                site: site.id,
                                                tab: { type: 'preview' },
                                            },
                                        }}
                                    >
                                        <SitePreviewIframe
                                            // Force the iframe to reload when the site changes
                                            key={site.id}
                                            site={site}
                                            organization={organization}
                                            className="aspect-video w-full rounded border"
                                            scaleResolution={{
                                                width: 1280,
                                                height: 1280 / (16 / 9),
                                            }}
                                        />
                                    </AtomLink>
                                    <div className="flex flex-col gap-4 px-2">
                                        <div className="flex flex-col gap-2">
                                            {!isLegacySiteType(site.type) ? (
                                                <SiteTypeHoverCard
                                                    organization={organization}
                                                    site={site}
                                                >
                                                    <SiteTypeBadge type={site.type} />
                                                </SiteTypeHoverCard>
                                            ) : null}
                                            <h1 className="text-ui-heading-large">{site.title}</h1>
                                        </div>
                                        {site.urls.published ? (
                                            <DescriptionList>
                                                <DescriptionTerm>URL</DescriptionTerm>
                                                <DescriptionDetail>
                                                    <DescriptionListIcon icon={Icon.Globe} />
                                                    <CopyableURL url={site.urls.published} />
                                                </DescriptionDetail>
                                            </DescriptionList>
                                        ) : null}
                                        {site.visibility === api.SiteVisibility.ShareLink ? (
                                            <DescriptionList>
                                                <DescriptionTerm>Share links</DescriptionTerm>
                                                <DescriptionDetail>
                                                    <ShareLinks
                                                        site={site}
                                                        organization={organization}
                                                    />
                                                </DescriptionDetail>
                                            </DescriptionList>
                                        ) : null}
                                        <div className="flex gap-2">
                                            <DescriptionList className="flex-1">
                                                <DescriptionTerm>Status</DescriptionTerm>
                                                <DescriptionDetail>
                                                    <DescriptionListIcon
                                                        icon={
                                                            site.published
                                                                ? Icon.DocsSitePublished
                                                                : Icon.DocsSiteUnpublished
                                                        }
                                                    />
                                                    {site.published ? 'Published' : 'Unpublished'}
                                                </DescriptionDetail>
                                            </DescriptionList>
                                            <DescriptionList className="flex-1">
                                                <DescriptionTerm>Audience</DescriptionTerm>
                                                <DescriptionDetail>
                                                    <DescriptionListIcon icon={VisibilityIcon} />
                                                    {getSiteVisibilityLabel(site.visibility)}
                                                </DescriptionDetail>
                                            </DescriptionList>
                                        </div>
                                    </div>
                                </div>
                                <SiteStructureOverview
                                    organization={organization}
                                    site={site}
                                    siteStructure={siteStructure}
                                    sitePermissions={sitePermissions}
                                />
                            </Card>
                        </div>
                        <div className="col-span-6 flex flex-col gap-8 lg:col-span-3 xl:col-span-4">
                            <SiteTrialBanner organization={organization} site={site} />
                            <BasicSiteUsingPaidFeaturesAlert
                                site={site}
                                sitePermissions={sitePermissions}
                            />
                            <AdsReviewStatusAlert organizationId={organization.id} site={site} />
                            <SiteImportBanner organization={organization} site={site} />
                            {sitePermissions.canAdminSite && (
                                <SiteGetStarted
                                    // We don't want to to preserve the state of the getting started between sites
                                    // it does not make sense to animate or keep the toggled items
                                    key={site.id}
                                    organization={organization}
                                    site={site}
                                    siteCustomization={siteCustomization}
                                    siteStructure={siteStructure}
                                    orgUsageMembers={orgUsageMembers}
                                />
                            )}
                            <SiteInsightsOverview organizationId={organization.id} site={site} />
                            {sitePermissions.canAdminSite && (
                                <Suspense fallback={null}>
                                    <SiteOverviewFeatured
                                        organizationId={organization.id}
                                        site={site}
                                        siteStructure={siteStructure}
                                    />
                                </Suspense>
                            )}
                        </div>
                    </main>
                </Layout>
            </PageGradientWallpaper>
        </Page>
    );
}

function ShareLinks(props: { site: api.Site; organization: api.Organization }) {
    const { site, organization } = props;
    const { data: shareLinks } = useSuspenseQuery(
        apiQuery('listSiteShareLinks', {
            organizationId: organization.id,
            siteId: site.id,
            limit: 10,
        })
    );

    const validShareLinks = shareLinks.items
        .filter((link) => link.active && link.urls.published)
        .slice(0, 3);

    return (
        <ul>
            {validShareLinks.map((link) => {
                assert(link.urls.published, 'Already filtered');
                return (
                    <li key={link.id} className="truncate">
                        <Link href={link.urls.published}>
                            <PrettyURL>{link.urls.published}</PrettyURL>
                        </Link>
                    </li>
                );
            })}
            <li className={clsx(validShareLinks.length > 0 && 'mt-1')}>
                <SiteOverviewSectionLink
                    href={{
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: site.id,
                            tab: { type: 'settings', section: 'audience' },
                        },
                    }}
                >
                    View all
                </SiteOverviewSectionLink>
            </li>
        </ul>
    );
}

function SiteOverviewFeatured(props: {
    site: api.Site;
    siteStructure: api.SiteStructure;
    organizationId: string;
}) {
    const { site, siteStructure, organizationId } = props;
    const isGetStartedDismissed = useIsSiteGetStartedDismissed(site.id);

    if (!isGetStartedDismissed) {
        return null;
    }

    return (
        <SiteRandomFeatureCard
            site={site}
            siteStructure={siteStructure}
            organizationId={organizationId}
        />
    );
}
